import * as React from 'react';
import Checkbox from 'antd/lib/checkbox';
import Dropdown from 'antd/lib/dropdown/dropdown';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import { CaretDownOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';

import { RootState } from '../../reducers/reducers';
import { DestinationOptions, DestinationOptionsAction } from '../../actions/release';
import { selectDestinationInfoByDestinationStepConfigId } from '../../selectors/selectors';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { antGrey } from '../styles/globalStyles';

interface DestinationOptionsSelectorProps {
    appId: string;
    value?: DestinationOptions;
    onChange?: (value: DestinationOptions | undefined) => void;
    disabled?: boolean;
}

export const DestinationOptionsSelector: React.FC<DestinationOptionsSelectorProps> = ({
    appId,
    value,
    onChange,
    disabled,
}) => {
    const destinationInfoByStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const allDestinations = React.useMemo(
        () => Object.values(destinationInfoByStepConfigId),
        [destinationInfoByStepConfigId],
    );

    const action: DestinationOptionsAction = value?.action ?? 'include';
    const selectedDestinationIds = value?.destinationIds ?? [];
    const [showSpecificDestinations, setShowSpecificDestinations] = React.useState(selectedDestinationIds.length > 0);

    React.useEffect(() => {
        if (selectedDestinationIds.length > 0) {
            setShowSpecificDestinations(true);
        }
    }, [selectedDestinationIds.length]);

    const handleToggleAllDestinations = (checked: boolean) => {
        if (checked) {
            setShowSpecificDestinations(false);
            onChange?.(undefined);
        } else {
            setShowSpecificDestinations(true);
        }
    };

    const handleActionChange = (newAction: DestinationOptionsAction) => {
        onChange?.({ destinationIds: selectedDestinationIds, action: newAction });
    };

    const handleDestinationsChange = (destinationIds: string[]) => {
        if (destinationIds.length === 0) {
            onChange?.(undefined);
            return;
        }
        onChange?.({ destinationIds, action });
    };

    // Scoping only makes sense with multiple destinations — nothing to filter otherwise.
    if (allDestinations.length <= 1) {
        return null;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Checkbox
                checked={!showSpecificDestinations}
                onChange={(e) => handleToggleAllDestinations(e.target.checked)}
                disabled={disabled}
            >
                Create for all destinations
            </Checkbox>

            {showSpecificDestinations && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Dropdown
                            trigger={['click']}
                            disabled={disabled}
                            menu={{
                                items: [
                                    { key: 'include', label: 'Include' },
                                    { key: 'exclude', label: 'Exclude' },
                                ],
                                onClick: (item) => handleActionChange(item.key as DestinationOptionsAction),
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    width: 110,
                                    padding: '4px 11px',
                                    border: `1px solid ${antGrey.grey5}`,
                                    borderRadius: 6,
                                }}
                            >
                                <Text>{action === 'include' ? 'Include' : 'Exclude'}</Text>
                                <CaretDownOutlined style={{ color: antGrey.grey7, fontSize: 12, marginLeft: 8 }} />
                            </div>
                        </Dropdown>
                        <Text>selected destinations:</Text>
                    </div>
                    <Select
                        mode="multiple"
                        style={{ minWidth: 200, width: '100%' }}
                        placeholder="Select destinations"
                        optionLabelProp="label"
                        maxTagCount={'responsive'}
                        value={selectedDestinationIds}
                        onChange={handleDestinationsChange}
                        disabled={disabled}
                        tagRender={(tagProps) => {
                            const { value: tagValue, closable, onClose } = tagProps;
                            const destination = allDestinations.find((d) => d.stepConfigId === tagValue);
                            if (!destination) return <span>{tagValue}</span>;
                            return (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        backgroundColor: antGrey.grey3,
                                        borderRadius: 4,
                                        padding: '2px 4px',
                                        marginRight: 4,
                                        border: `1px solid ${antGrey.grey5}`,
                                    }}
                                >
                                    <DestinationCounter destinations={[destination]} size={18} />
                                    <span>{destination.name || destination.appName}</span>
                                    {closable && (
                                        <span
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            onClick={onClose}
                                            style={{ cursor: 'pointer', marginLeft: 2, color: antGrey.grey7 }}
                                        >
                                            ×
                                        </span>
                                    )}
                                </span>
                            );
                        }}
                    >
                        {allDestinations.map((destination) => (
                            <Select.Option
                                key={destination.stepConfigId}
                                value={destination.stepConfigId}
                                label={destination.name || destination.appName}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <DestinationCounter destinations={[destination]} size={20} />
                                    <span>{destination.name || destination.appName}</span>
                                </div>
                            </Select.Option>
                        ))}
                    </Select>
                </>
            )}
        </div>
    );
};
